import {
  ForwardedRef,
  forwardRef,
  lazy,
  MutableRefObject,
  ReactElement,
  ReactNode,
  Suspense,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Editor } from '@tiptap/core';
import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Link from '@tiptap/extension-link';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import { EditorContent, Extensions, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import styled from 'styled-components';

import { Button } from '@components/button';
import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { IconTextFormatBold } from '@components/icons/text-format-bold';
import { IconTextFormatBullet } from '@components/icons/text-format-bullet';
import { IconTextFormatItalic } from '@components/icons/text-format-italic';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { IconTextFormatList } from '@components/icons/text-format-list';
import { IconTextFormatSize } from '@components/icons/text-format-size';
import { IconTextFormatUnderline } from '@components/icons/text-format-underline';
import { Stack } from '@components/stack';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { NbspReplacer } from './addons/nbsp-replacer';
import OneLiner from './addons/one-liner';

const LinkButton = lazy(() => import('./addons/link-button'));

export type EditorValue<T = {}> = { value: string } & T;

export type BaseTextEditorProps<T = {}> = {
  onChange?(value: EditorValue<T>): void;
  onFocus?(e: React.FocusEvent<HTMLDivElement>): void;
  onEditorBlur?(editor: Editor): void;
  singleLine?: boolean;
  plaintext?: boolean;
  label?: string;
  hideLabel?: boolean;
  labelIcon?: ReactElement<IconProps>;
  labelRightContent?: ReactNode;
  placeholder?: string;
  error?: string;
  actions?: ReactNode;
  extensions?: Extensions;
  minHeight?: string;
  disabled?: boolean;
  info?: string;
  className?: string;
};

export type TextEditorProps<T = {}> = BaseTextEditorProps<T> & {
  defaultValue?: EditorValue<T>;
  helpText?: string;
  isActive?: boolean;
};

export type EditorMethods = {
  insertContent(content: string): void;
  replaceContent(content: string): void;
};

const escapeHtml = (content: string = '', whitelist: Array<string>) => {
  if (!content) {
    return content;
  }
  return content.replace(/<\/?([a-zA-Z0-9-]+)(\s[^<>]*)?>/g, (match, tagName) => {
    if (whitelist.includes(tagName)) {
      return match;
    } else {
      return match.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
  });
};

export const TextEditor = forwardRef(
  (
    {
      actions,
      extensions = [],
      defaultValue,
      plaintext = false,
      singleLine = false,
      onEditorBlur,
      onChange,
      onFocus,
      error,
      label,
      labelIcon,
      hideLabel = false,
      disabled = false,
      minHeight = singleLine ? undefined : plaintext ? '6.5rem' : '4rem',
      placeholder,
      info,
      helpText = '',
      isActive = false,
      labelRightContent,
      ...rest
    }: TextEditorProps,
    ref: ForwardedRef<EditorMethods>
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const editorRef: MutableRefObject<Editor | null> = useRef(null);
    const commonExtensions = [
      Placeholder.configure({
        placeholder,
        showOnlyWhenEditable: false,
      }),
      ...extensions,
    ];

    useImperativeHandle(ref, () => ({
      insertContent: (content) => {
        if (editorRef.current?.isEditable) {
          editorRef.current?.chain().focus().insertContent(content).run();
        } else {
          // If the editor is not editable, it is also not focusable, and causes UX glitches when trying to focus it
          editorRef.current?.chain().insertContent(content).run();
        }
      },
      replaceContent: (content) => {
        editorRef.current?.chain().focus().clearContent().insertContent(content).run();
      },
    }));

    const editor = useEditor({
      editable: !disabled,
      onBlur: ({ editor }) => onEditorBlur?.(editor),
      parseOptions: {
        preserveWhitespace: 'full',
      },
      ...(plaintext
        ? {
            content: escapeHtml(
              defaultValue?.value,
              commonExtensions.map((extension) => extension.config.tag)
            ),
            extensions: [
              singleLine
                ? OneLiner
                : Document.extend({
                    // Solution to allow new lines in plaintext, based on
                    // https://discuss.prosemirror.net/t/allow-n-in-text-nodes/6354
                    content: 'inline*',
                    whitespace: 'pre',
                    addKeyboardShortcuts() {
                      return {
                        Enter: () => this.editor.commands.insertContent('\n'),
                      };
                    },
                  }),
              Paragraph,
              Text,
              NbspReplacer,
              History,
              ...commonExtensions,
            ],
            onUpdate: ({ editor }) => onChange?.({ value: editor.getText() }),
          }
        : {
            content: defaultValue?.value,
            extensions: [
              StarterKit,
              Underline,
              Link.configure({
                openOnClick: false,
              }),
              ...commonExtensions,
            ],

            onUpdate: ({ editor }) => onChange?.({ value: editor.getHTML() }),
          }),
    });

    const toolbarButtons = useMemo(
      () =>
        plaintext || !editor
          ? []
          : [
              {
                id: 'heading',
                icon: <IconTextFormatSize />,
                onClick: () => editor?.chain().focus().toggleHeading({ level: 3 }).run(),
              },
              {
                id: 'bold',
                icon: <IconTextFormatBold />,
                onClick: () => editor?.chain().focus().toggleBold().run(),
              },
              {
                id: 'italic',
                icon: <IconTextFormatItalic />,
                onClick: () => editor?.chain().focus().toggleItalic().run(),
              },
              {
                id: 'underline',
                icon: <IconTextFormatUnderline />,
                onClick: () => editor?.chain().focus().toggleUnderline().run(),
              },
              {
                id: 'bulletList',
                icon: <IconTextFormatBullet />,
                onClick: () => editor?.chain().focus().toggleBulletList().run(),
              },
              {
                id: 'orderedList',
                icon: <IconTextFormatList />,
                onClick: () => editor?.chain().focus().toggleOrderedList().run(),
              },
            ],
      [editor, plaintext]
    );

    editorRef.current = editor;

    const formLabel = <FormLabel text={label} icon={labelIcon} info={info} helpText={helpText} />;
    const labelRow = labelRightContent ? (
      <Stack orientation="horizontal" align="center" justify="space-between">
        {formLabel}
        {labelRightContent}
      </Stack>
    ) : (
      formLabel
    );

    return (
      <Stack space={1} {...rest}>
        {label && !hideLabel && labelRow}
        <Container
          $isFocused={isFocused || isActive}
          $plaintext={plaintext}
          data-error={Boolean(error)}
        >
          {!plaintext && (
            <ToolbarContainer>
              <Stack orientation="horizontal">
                {toolbarButtons.map((button, index) => (
                  <ToolbarButton
                    type="button"
                    key={index}
                    onClick={button.onClick}
                    data-active={editor && editor.isActive(button.id)}
                  >
                    {button.icon}
                  </ToolbarButton>
                ))}
                <Suspense
                  fallback={<Button variant="ghost" isDisabled iconLeft={<IconTextFormatLink />} />}
                >
                  {editor && <LinkButton editor={editor} />}
                </Suspense>
              </Stack>
              <Stack orientation="horizontal">{actions}</Stack>
            </ToolbarContainer>
          )}
          <Stack
            orientation="horizontal"
            justify="space-between"
            onFocus={disabled ? onFocus : undefined}
            tabIndex={disabled ? 0 : undefined}
          >
            <StyledEditor
              editor={editor}
              onFocus={(e) => {
                if (!disabled) {
                  onFocus?.(e);
                }
                setIsFocused(true);
              }}
              onBlur={() => setIsFocused(false)}
              $minHeight={minHeight}
              $plaintext={plaintext}
            />
            {plaintext && actions}
          </Stack>
        </Container>
        <FormError text={error} />
      </Stack>
    );
  }
);

export const padding = 8;

export const borderWidth = 1;

const Container = styled(Stack)<{
  $isFocused: boolean;
  $plaintext: TextEditorProps['plaintext'];
}>(({ $isFocused, $plaintext }) => ({
  minHeight: pxToRem(40),
  color: theme.color.textPrimary,
  transition: 'all 150ms ease-out',
  backgroundColor: $isFocused ? theme.form.bg.colorFocus : theme.form.bg.color,
  borderRadius: theme.form.border.radius,
  padding: $plaintext ? pxToRem(padding - borderWidth) : pxToRem(padding * 2),
  border: `${theme.form.border.width} solid ${
    $isFocused ? theme.form.border.colorFocus : theme.form.border.color
  }`,
  ':hover': {
    backgroundColor: $isFocused ? theme.form.bg.colorFocus : theme.form.bg.colorHover,
  },
  '&[data-error="true"]': {
    borderColor: theme.color.error,
  },
  ...($plaintext && {
    p: {
      marginBlock: '0px',
    },
  }),
}));

const ToolbarContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
});

const StyledEditor = styled(EditorContent)<{
  $minHeight?: string;
  $plaintext?: boolean;
}>(({ $minHeight, $plaintext }) => ({
  flex: 1,
  '.ProseMirror': {
    minHeight: $minHeight ?? 'auto',
    lineHeight: pxToRem(24),
    outline: 'none',
    overflowWrap: 'anywhere',
    padding: pxToRem(0),
    ...($plaintext && {
      padding: `${pxToRem(0)} ${pxToRem(12)}`,
    }),
    'p.is-editor-empty:first-child::before': {
      color: theme.color.textHelper,
      font: theme.font.regular14,
      content: 'attr(data-placeholder)',
    },

    a: {
      color: `${theme.color.elementBlue100} !important`,
      textDecoration: 'underline !important',
    },
  },
  p: {
    margin: 0,
    padding: 0,

    '&:empty': {
      padding: '0.75rem 0',
    },
  },
  'li > p': {
    margin: 0,
  },
}));

export const ToolbarButton = styled.button({
  transition: 'all 150ms ease-out',
  borderRadius: '0.25rem',
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: theme.color.elementPurple50,
  height: theme.base.height,
  width: theme.base.height,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 'none',

  '&:focus-visible': {
    boxShadow: theme.shadow.focus,
    backgroundColor: theme.color.uiTrans10,
    color: theme.color.purple80,
    '--shadow-focus-color': theme.color.purple50,
    '--shadow-focus-gap': 0,
    '--shadow-focus-width': '0.1rem',
  },

  '&:active': {
    transform: 'scale(0.97)',
  },

  '&[data-active="true"]': {
    color: theme.color.elementBlue100,
  },

  '&:hover': {
    cursor: 'pointer',
    backgroundColor: theme.color.uiTrans10,

    '&[data-active="false"]': {
      color: theme.color.purple80,
    },
  },
});
